import { DEFAULT_MENU_SETTINGS, menuTemplate } from './index.js';
import { EXTRA_STARTERS } from './starters-extra.js';
import { NIGHT_BITES_STARTERS } from './starters-night-bites.js';
import { PIZZA_SHOW_STARTERS } from './starters-pizza-show.js';
import { FOOD_COLLAGE_STARTERS } from './starters-food-collage.js';
import { BRUSH_BURGER_STARTERS } from './starters-brush-burger.js';
import { STACK_PANELS_STARTERS } from './starters-stack-panels.js';
import { FRESH_DRINKS_STARTERS } from './starters-fresh-drinks.js';
import { CHALK_CHILI_STARTERS } from './starters-chalk-chili.js';
import { WALNUT_STARTERS } from './starters-walnut.js';
import { TANGERINE_STARTERS } from './starters-tangerine.js';
import { INK_WALL_STARTERS } from './starters-ink-wall.js';
import { BLOCKS_STARTERS } from './starters-blocks.js';
import type { BlocksLayout } from './blocks.js';
import type { MenuBoardContent, MenuBrand, MenuItemContent, MenuSection, MenuVariant } from './types.js';

/**
 * Starter menus: a complete, styled board for every design, with real dishes, prices and photos,
 * so a new board looks finished from the first second and people only change what is theirs.
 *
 * Photos are named after files in `apps/api/assets/menu-starters` (free Unsplash photos; see
 * CREDITS.md there). On the board they appear as `starter:<name>` until the API imports them
 * into the organization's media library.
 */

export interface StarterItem {
  key: string;
  section: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  price: number;
  discountPrice?: number;
  photo: string;
  category: string;
  ingredients?: string[];
  ingredientsAr?: string[];
  variants?: MenuVariant[];
  calories?: number;
  badge?: string;
  badgeAr?: string;
}

export interface MenuStarter {
  title: string;
  titleAr: string;
  brand: Omit<MenuBrand, 'logoMediaId'>;
  sections: (Omit<MenuSection, 'id' | 'imageMediaId'> & { key: string; photo?: string })[];
  items: StarterItem[];
  featured?: string;
  decor: string[];
  /** For "Build your own": the sample layout; its section ids are the section keys above. */
  blocks?: BlocksLayout;
}

const v = (label: string, labelAr: string, detail: string | null, price: number): MenuVariant => ({
  label,
  labelAr,
  detail,
  price,
});

export const MENU_STARTERS: Record<string, MenuStarter> = {
  'cafe-panels': {
    title: 'Café menu',
    titleAr: 'قائمة المقهى',
    brand: { name: 'Olive & Oak', nameAr: 'زيتون وبلوط', tagline: 'Coffee house', taglineAr: 'بيت القهوة' },
    sections: [
      { key: 'specials', title: 'Autumn', titleAr: 'عروض الخريف', subtitle: 'Specials', subtitleAr: 'لفترة محدودة' },
      { key: 'allday', title: 'All day menu', titleAr: 'طوال اليوم', subtitle: null, subtitleAr: null },
    ],
    featured: 'latte',
    items: [
      { key: 'latte', section: 'specials', title: 'Caramel Latte', titleAr: 'لاتيه بالكراميل', description: 'Smooth espresso with caramel and steamed milk', descriptionAr: 'إسبريسو ناعم مع الكراميل والحليب المبخر', price: 3.75, photo: 'latte', category: 'Coffee' },
      { key: 'soup', section: 'specials', title: 'Pumpkin soup', titleAr: 'شوربة اليقطين', description: 'Roasted pumpkin, feta and toasted seeds', descriptionAr: 'يقطين مشوي مع جبنة الفيتا والبذور', price: 4.25, photo: 'pumpkin-soup', category: 'Brunch' },
      { key: 'pancakes', section: 'specials', title: 'Berry pancakes', titleAr: 'بان كيك بالتوت', description: 'Fluffy stack with blueberries and maple', descriptionAr: 'بان كيك هش مع التوت وشراب القيقب', price: 4.75, photo: 'pancakes', category: 'Brunch' },
      { key: 'granola', section: 'specials', title: 'Harvest granola', titleAr: 'جرانولا الحصاد', description: 'Yoghurt, granola, berries and honey', descriptionAr: 'لبن وجرانولا وتوت وعسل', price: 3.95, photo: 'granola-bowl', category: 'Brunch' },
      { key: 'avocado', section: 'allday', title: 'Avocado toast', titleAr: 'توست الأفوكادو', description: 'Smashed avocado, chili flakes, poached egg', descriptionAr: 'أفوكادو مهروس مع الفلفل وبيضة مسلوقة', price: 6.95, photo: 'avocado-toast', category: 'Brunch' },
      { key: 'bagel', section: 'allday', title: 'Smoked salmon bagel', titleAr: 'بيغل السلمون المدخن', description: 'Cream cheese, rocket, red onion, capers', descriptionAr: 'جبنة كريمية وجرجير وبصل أحمر', price: 7.25, photo: 'salmon-bagel', category: 'Brunch' },
      { key: 'panini', section: 'allday', title: 'Chicken pesto panini', titleAr: 'بانيني دجاج بالبيستو', description: 'Mozzarella, sun-dried tomatoes, basil pesto', descriptionAr: 'موزاريلا وطماطم مجففة وبيستو', price: 7.95, photo: 'panini', category: 'Sandwiches' },
      { key: 'benedict', section: 'allday', title: 'Eggs Benedict', titleAr: 'بيض بينيديكت', description: 'Poached eggs, hollandaise, toasted muffin', descriptionAr: 'بيض مسلوق مع صلصة الهولنديز', price: 7.5, photo: 'eggs-benedict', category: 'Brunch', badge: 'NEW', badgeAr: 'جديد' },
      { key: 'croissant', section: 'allday', title: 'Butter croissant', titleAr: 'كرواسون بالزبدة', description: 'Baked fresh every morning', descriptionAr: 'يُخبز طازجاً كل صباح', price: 2.25, photo: 'croissant', category: 'Bakery' },
    ],
    decor: [],
  },

  'bold-sections': {
    title: 'Street kitchen',
    titleAr: 'مطبخ الشارع',
    brand: { name: 'Street Kitchen', nameAr: 'مطبخ الشارع', tagline: null, taglineAr: null },
    sections: [
      { key: 'tacos', title: 'Tacos', titleAr: 'تاكو', subtitle: "Let's taco 'bout it", subtitleAr: 'تاكو طازج', photo: 'beef-tacos' },
      { key: 'soups', title: 'Soups', titleAr: 'شوربات', subtitle: 'Made daily', subtitleAr: 'تُحضّر يومياً', photo: 'tomato-soup' },
      { key: 'flatbreads', title: 'Flat breads', titleAr: 'خبز مسطّح', subtitle: 'Stone baked', subtitleAr: 'مخبوز على الحجر' },
    ],
    items: [
      { key: 'beef', section: 'tacos', title: 'Spicy beef', titleAr: 'لحم حار', description: 'Brown rice, beef, salsa, onions, roasted peppers', descriptionAr: 'أرز ولحم وصلصة وفلفل مشوي', price: 4.5, photo: 'beef-tacos', category: 'Tacos', badge: '3 TACOS', badgeAr: '٣ قطع', calories: 640 },
      { key: 'chicken', section: 'tacos', title: 'Lime chicken', titleAr: 'دجاج بالليمون', description: 'Lime chicken, salsa, cabbage, jalapeños, cilantro', descriptionAr: 'دجاج بالليمون وملفوف وهالبينو', price: 4.25, photo: 'chicken-tacos', category: 'Tacos', badge: '3 TACOS', badgeAr: '٣ قطع', calories: 590 },
      { key: 'fish', section: 'tacos', title: 'Crispy fish', titleAr: 'سمك مقرمش', description: 'Battered fish, avocado, chipotle mayo', descriptionAr: 'سمك مقرمش مع أفوكادو ومايونيز', price: 4.95, photo: 'fish-tacos', category: 'Tacos', badge: '3 TACOS', badgeAr: '٣ قطع' },
      { key: 'green', section: 'soups', title: 'Green garden', titleAr: 'شوربة الخضار', description: 'Broccoli, peas and herbs, creamy and fresh', descriptionAr: 'بروكلي وبازيلاء وأعشاب', price: 2.5, photo: 'broccoli-soup', category: 'Soups', variants: [v('Small', 'صغير', '12 oz', 2.5), v('Regular', 'وسط', '16 oz', 3.25)] },
      { key: 'tomato', section: 'soups', title: 'Roast tomato', titleAr: 'طماطم مشوية', description: 'Slow-roasted tomatoes, basil, cream', descriptionAr: 'طماطم مشوية مع الريحان', price: 2.25, photo: 'tomato-soup', category: 'Soups', variants: [v('Small', 'صغير', '12 oz', 2.25), v('Regular', 'وسط', '16 oz', 3)] },
      { key: 'pepperoni', section: 'flatbreads', title: 'Pepperoni', titleAr: 'ببروني', description: 'Pizza sauce, beef pepperoni, mozzarella', descriptionAr: 'صلصة بيتزا وببروني وموزاريلا', price: 5.5, photo: 'pepperoni-pizza', category: 'Flat breads' },
      { key: 'margherita', section: 'flatbreads', title: 'Margherita', titleAr: 'مارغريتا', description: 'Tomato, fresh mozzarella, basil', descriptionAr: 'طماطم وموزاريلا طازجة وريحان', price: 4.95, photo: 'margherita-pizza', category: 'Flat breads' },
      { key: 'garden', section: 'flatbreads', title: 'Garden veggie', titleAr: 'خضار الحديقة', description: 'Peppers, olives, onions, rocket', descriptionAr: 'فلفل وزيتون وبصل وجرجير', price: 5.25, photo: 'veggie-flatbread', category: 'Flat breads' },
    ],
    decor: ['margherita-pizza'],
  },

  chalkboard: {
    title: 'Burger menu',
    titleAr: 'قائمة البرجر',
    brand: { name: 'Grill & Co', nameAr: 'جريل آند كو', tagline: 'Taste happiness, bite by bite', taglineAr: 'السعادة في كل قضمة' },
    sections: [
      { key: 'burgers', title: 'Burger menu', titleAr: 'البرجر', subtitle: '*Every burger comes with fries', subtitleAr: '*تقدّم جميع الوجبات مع البطاطا' },
      { key: 'sides', title: 'Sides', titleAr: 'مقبلات', subtitle: null, subtitleAr: null },
      { key: 'desserts', title: 'Desserts', titleAr: 'حلويات', subtitle: null, subtitleAr: null },
      { key: 'drinks', title: 'Drinks', titleAr: 'مشروبات', subtitle: null, subtitleAr: null },
    ],
    items: [
      { key: 'classic', section: 'burgers', title: 'Classic Jack', titleAr: 'كلاسيك جاك', description: 'Juicy beef, cheddar, lettuce, tomato', descriptionAr: 'لحم بقري وشيدر وخس وطماطم', price: 5.5, photo: 'burger', category: 'Burgers' },
      { key: 'spicy', section: 'burgers', title: 'Hot & spicy', titleAr: 'حار جداً', description: 'Fiery peppers, zesty sauce, bold kick', descriptionAr: 'فلفل حار وصلصة لاذعة', price: 6.25, photo: 'cheeseburger', category: 'Burgers', badge: 'HOT', badgeAr: 'حار' },
      { key: 'chicken', section: 'burgers', title: 'Crispy chicken', titleAr: 'دجاج مقرمش', description: 'Golden chicken, lettuce, tangy mayo', descriptionAr: 'دجاج ذهبي وخس ومايونيز', price: 5.75, photo: 'chicken-burger', category: 'Burgers' },
      { key: 'veggie', section: 'burgers', title: 'Veggie bliss', titleAr: 'برجر نباتي', description: 'Grilled veggies, avocado, fresh greens', descriptionAr: 'خضار مشوية وأفوكادو', price: 5.25, photo: 'veggie-burger', category: 'Burgers' },
      { key: 'smoky', section: 'burgers', title: 'Smokey supreme', titleAr: 'سموكي سوبريم', description: 'Bacon, BBQ sauce, cheese, smoky crunch', descriptionAr: 'لحم مقدد وصلصة باربكيو وجبنة', price: 6.95, photo: 'bacon-burger', category: 'Burgers' },
      { key: 'fries', section: 'sides', title: 'Golden fries', titleAr: 'بطاطا ذهبية', description: 'Crispy and lightly salted', descriptionAr: 'مقرمشة ومملحة قليلاً', price: 1.75, photo: 'fries', category: 'Sides' },
      { key: 'rings', section: 'sides', title: 'Onion rings', titleAr: 'حلقات البصل', description: 'Beer-battered and crunchy', descriptionAr: 'مقرمشة ولذيذة', price: 2.25, photo: 'onion-rings', category: 'Sides' },
      { key: 'sundae', section: 'desserts', title: 'Sundae', titleAr: 'صنداي', description: 'Ice cream, toppings, whipped cream', descriptionAr: 'آيس كريم مع الإضافات والكريمة', price: 2.95, photo: 'sundae', category: 'Desserts' },
      { key: 'tiramisu', section: 'desserts', title: 'Tiramisu', titleAr: 'تيراميسو', description: 'Coffee-soaked layers, mascarpone, cocoa', descriptionAr: 'طبقات بالقهوة والماسكاربوني', price: 3.75, photo: 'tiramisu', category: 'Desserts' },
      { key: 'brownie', section: 'desserts', title: 'Lava brownie', titleAr: 'براوني', description: 'Warm chocolate, molten centre', descriptionAr: 'شوكولاتة دافئة بقلب ذائب', price: 3.25, photo: 'brownie', category: 'Desserts' },
      { key: 'shake', section: 'drinks', title: 'Creamy milkshake', titleAr: 'ميلك شيك', description: 'Thick, sweet, indulgent', descriptionAr: 'كثيف وحلو ولذيذ', price: 3.25, photo: 'milkshake', category: 'Drinks' },
      { key: 'tea', section: 'drinks', title: 'Lemon iced tea', titleAr: 'شاي مثلج بالليمون', description: 'Citrusy, cool, refreshing', descriptionAr: 'منعش بنكهة الليمون', price: 1.75, photo: 'iced-tea', category: 'Drinks' },
      { key: 'cola', section: 'drinks', title: 'Craft cola', titleAr: 'كولا', description: 'Bold, sweet, rich, fizzy', descriptionAr: 'غازية ومنعشة', price: 1.25, photo: 'cola', category: 'Drinks' },
    ],
    decor: ['burger', 'cheeseburger', 'fries', 'onion-rings'],
  },

  'dark-ribbon': {
    title: 'Burger & pizza house',
    titleAr: 'بيت البرجر والبيتزا',
    brand: { name: 'Burger & Pizza House', nameAr: 'بيت البرجر والبيتزا', tagline: null, taglineAr: null },
    sections: [
      { key: 'special', title: 'Special menu', titleAr: 'قائمة مميزة', subtitle: null, subtitleAr: null },
      { key: 'fast', title: 'Fast food', titleAr: 'وجبات سريعة', subtitle: null, subtitleAr: null },
      { key: 'drinks', title: 'Special drinks', titleAr: 'مشروبات مميزة', subtitle: null, subtitleAr: null },
    ],
    items: [
      { key: 'double', section: 'special', title: 'Double cheeseburger', titleAr: 'دبل تشيز برجر', description: 'Two beef patties, double cheddar, pickles', descriptionAr: 'قطعتا لحم وجبنة شيدر مضاعفة', price: 6.5, discountPrice: 4.95, photo: 'cheeseburger', category: 'Burgers' },
      { key: 'classic', section: 'special', title: 'Classic burger', titleAr: 'برجر كلاسيك', description: 'Beef, lettuce, tomato, house sauce', descriptionAr: 'لحم وخس وطماطم وصلصة خاصة', price: 4.75, photo: 'burger', category: 'Burgers' },
      { key: 'chicken', section: 'special', title: 'Crispy chicken', titleAr: 'دجاج مقرمش', description: 'Golden fried chicken, slaw, spicy mayo', descriptionAr: 'دجاج مقلي وسلطة ومايونيز حار', price: 4.95, photo: 'chicken-burger', category: 'Burgers' },
      { key: 'bacon', section: 'special', title: 'Smoky bacon', titleAr: 'برجر مدخن', description: 'Beef bacon, BBQ sauce, onion rings', descriptionAr: 'لحم مقدد وصلصة باربكيو', price: 5.95, photo: 'bacon-burger', category: 'Burgers' },
      { key: 'pepperoni', section: 'fast', title: 'Pepperoni pizza', titleAr: 'بيتزا ببروني', description: 'Tomato sauce, mozzarella, beef pepperoni', descriptionAr: 'صلصة طماطم وموزاريلا وببروني', price: 7.5, discountPrice: 5.95, photo: 'pepperoni-pizza', category: 'Pizza' },
      { key: 'margherita', section: 'fast', title: 'Margherita', titleAr: 'مارغريتا', description: 'Fresh mozzarella, tomato, basil', descriptionAr: 'موزاريلا طازجة وطماطم وريحان', price: 5.5, photo: 'margherita-pizza', category: 'Pizza' },
      { key: 'fries', section: 'fast', title: 'Loaded fries', titleAr: 'بطاطا محمّلة', description: 'Cheese sauce, jalapeños, crispy onions', descriptionAr: 'صلصة الجبنة والهالبينو', price: 2.95, photo: 'fries', category: 'Sides' },
      { key: 'rings', section: 'fast', title: 'Onion rings', titleAr: 'حلقات البصل', description: 'Golden, crunchy, with dip', descriptionAr: 'ذهبية ومقرمشة مع الصلصة', price: 2.25, photo: 'onion-rings', category: 'Sides' },
      { key: 'shake', section: 'drinks', title: 'Chocolate shake', titleAr: 'ميلك شيك شوكولاتة', description: 'Thick, creamy, whipped topping', descriptionAr: 'كثيف وكريمي مع الكريمة', price: 3.25, discountPrice: 2.5, photo: 'milkshake', category: 'Drinks' },
      { key: 'lemonade', section: 'drinks', title: 'Mint lemonade', titleAr: 'ليمون بالنعناع', description: 'Fresh lemon, mint, crushed ice', descriptionAr: 'ليمون طازج ونعناع وثلج', price: 2, photo: 'mint-lemonade', category: 'Drinks' },
      { key: 'tea', section: 'drinks', title: 'Iced tea', titleAr: 'شاي مثلج', description: 'Lemon, cool and refreshing', descriptionAr: 'بالليمون ومنعش', price: 1.75, photo: 'iced-tea', category: 'Drinks' },
      { key: 'cola', section: 'drinks', title: 'Cola', titleAr: 'كولا', description: 'Ice cold, served with lemon', descriptionAr: 'باردة مع الليمون', price: 1.25, photo: 'cola', category: 'Drinks' },
    ],
    decor: [],
  },

  'classic-grid': {
    title: 'Levant kitchen',
    titleAr: 'مطبخ الشام',
    brand: { name: 'Levant Kitchen', nameAr: 'مطبخ الشام', tagline: null, taglineAr: null },
    sections: [
      { key: 'grill', title: 'Grills & wraps', titleAr: 'مشاوي وساندويش', subtitle: null, subtitleAr: null },
      { key: 'mezze', title: 'Mezze', titleAr: 'مقبلات', subtitle: null, subtitleAr: null },
      { key: 'sweet', title: 'Sweets & drinks', titleAr: 'حلويات ومشروبات', subtitle: null, subtitleAr: null },
    ],
    items: [
      { key: 'shawarma', section: 'grill', title: 'Chicken shawarma', titleAr: 'شاورما دجاج', description: 'Garlic sauce, pickles, fries', descriptionAr: 'ثومية ومخلل وبطاطا', price: 3.75, photo: 'shawarma', category: 'Grills', badge: 'BEST SELLER', badgeAr: 'الأكثر طلباً' },
      { key: 'grill', section: 'grill', title: 'Mixed grill', titleAr: 'مشاوي مشكلة', description: 'Kebab, shish tawook, grilled vegetables', descriptionAr: 'كباب وشيش طاووق وخضار مشوية', price: 9.5, discountPrice: 8.5, photo: 'mixed-grill', category: 'Grills' },
      { key: 'falafel', section: 'grill', title: 'Falafel plate', titleAr: 'صحن فلافل', description: 'With tahini, pickles and fresh bread', descriptionAr: 'مع الطحينة والمخلل والخبز', price: 2.5, photo: 'falafel', category: 'Grills' },
      { key: 'hummus', section: 'mezze', title: 'Hummus', titleAr: 'حمص', description: 'Chickpeas, tahini, olive oil, paprika', descriptionAr: 'حمص وطحينة وزيت زيتون', price: 2.25, photo: 'hummus', category: 'Mezze' },
      { key: 'salad', section: 'mezze', title: 'Garden salad', titleAr: 'سلطة الحديقة', description: 'Fresh vegetables, chickpeas, lemon dressing', descriptionAr: 'خضار طازجة وحمص وليمون', price: 3.25, photo: 'fattoush', category: 'Mezze' },
      { key: 'nest', section: 'sweet', title: 'Pistachio nest', titleAr: 'عش البلبل', description: 'Crisp kataifi with pistachios and syrup', descriptionAr: 'كنافة مقرمشة بالفستق والقطر', price: 3, photo: 'pistachio-pastry', category: 'Sweets' },
      { key: 'coffee', section: 'sweet', title: 'Turkish coffee', titleAr: 'قهوة تركية', description: 'Strong, rich, with cardamom', descriptionAr: 'قهوة قوية بالهيل', price: 1.5, photo: 'turkish-coffee', category: 'Drinks' },
      { key: 'lemonade', section: 'sweet', title: 'Mint lemonade', titleAr: 'ليمون بالنعناع', description: 'Fresh lemon, mint, crushed ice', descriptionAr: 'ليمون طازج ونعناع وثلج', price: 2, photo: 'mint-lemonade', category: 'Drinks', variants: [v('Regular', 'عادي', null, 2), v('Large', 'كبير', null, 2.75)] },
    ],
    decor: [],
  },
  ...EXTRA_STARTERS,
  ...NIGHT_BITES_STARTERS,
  ...PIZZA_SHOW_STARTERS,
  ...FOOD_COLLAGE_STARTERS,
  ...BRUSH_BURGER_STARTERS,
  ...STACK_PANELS_STARTERS,
  ...FRESH_DRINKS_STARTERS,
  ...CHALK_CHILI_STARTERS,
  ...WALNUT_STARTERS,
  ...TANGERINE_STARTERS,
  ...INK_WALL_STARTERS,
  ...BLOCKS_STARTERS,
};

/** Every photo a starter menu uses. */
export const MENU_STARTER_PHOTOS: string[] = [
  ...new Set(
    Object.values(MENU_STARTERS).flatMap((s) => [
      ...s.items.map((i) => i.photo),
      ...s.sections.flatMap((x) => (x.photo ? [x.photo] : [])),
      ...s.decor,
    ]),
  ),
].sort();

export const STARTER_PHOTO_PREFIX = 'starter:';

/** The photo file a `starter:<name>` id stands for, or null for an ordinary media id. */
export function starterPhotoName(mediaId: string): string | null {
  return mediaId.startsWith(STARTER_PHOTO_PREFIX) ? mediaId.slice(STARTER_PHOTO_PREFIX.length) : null;
}

const photo = (name: string | undefined) => (name ? `${STARTER_PHOTO_PREFIX}${name}` : null);

export function starterItemContent(item: StarterItem, sectionId: string | null): MenuItemContent {
  return {
    id: `starter-${item.key}`,
    title: item.title,
    titleAr: item.titleAr,
    description: item.description,
    descriptionAr: item.descriptionAr,
    ingredients: item.ingredients ?? [],
    ingredientsAr: item.ingredientsAr ?? [],
    price: item.price,
    discountPrice: item.discountPrice ?? null,
    imageMediaId: photo(item.photo),
    category: item.category,
    variants: item.variants ?? [],
    calories: item.calories ?? null,
    badge: item.badge ?? null,
    badgeAr: item.badgeAr ?? null,
    sectionId,
  };
}

/** A design's starter menu as a board, in the design's own look, for previews. */
export function starterBoard(templateId: string): MenuBoardContent {
  const template = menuTemplate(templateId);
  const starter = MENU_STARTERS[template.id]!;
  return {
    menuId: `starter-${template.id}`,
    name: starter.title,
    templateId: template.id,
    theme: template.defaultTheme,
    settings: { ...DEFAULT_MENU_SETTINGS, title: starter.title, titleAr: starter.titleAr, language: 'en' },
    items: starter.items.map((i) => starterItemContent(i, i.section)),
    sections: starter.sections.map(({ key, photo: picture, ...s }) => ({ ...s, id: key, imageMediaId: photo(picture) })),
    featuredItemId: starter.featured ? `starter-${starter.featured}` : null,
    brand: { ...starter.brand, logoMediaId: null },
    decorMediaIds: starter.decor.map((d) => photo(d)!),
    blocks: starter.blocks ? (JSON.parse(JSON.stringify(starter.blocks)) as BlocksLayout) : null,
  };
}
